import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Stack,
  Checkbox,
  ListItemText,
  InputAdornment,
  Autocomplete,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Search, UploadIcon, UserPlus, X } from "lucide-react";
import AddIcon from "@mui/icons-material/Add";
import { axiosCall } from "../utils/axiosCall";
import constants from "../config/constants";
import { useSelector } from "react-redux";
import { useNavigate, useNavigation, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import Information from "../comps/Information";
import PatientAppointments from "../comps/PatientAppointments";
import PatientVisits from "../comps/PatientVisits";
import axios from "axios";
import config from "../config/config";

function PatientInfo() {
  const user = useSelector((state) => state.user);
  const [active, setActive] = useState("Information");
  const [appointmentData, setAppointmentData] = useState({});
  const [selectedVisitData, setSelectedVisitData] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [showDepartment, setShowDepartment] = useState(false); // Default to false (No)
  const [pendingPrintData, setPendingPrintData] = useState(null);
  const { patientId } = useParams();
  const navigation = useNavigate();

  // Handler functions must be defined here, not inside any other function or block
  const handlePrintOPDCasePaper = async (dataToPrint) => {
    try {
      const apiUrl = `${config.server.url}/apis/${constants.GET_OPD_CASE_PAPER}/${dataToPrint}?showDepartment=${showDepartment}`;
      const response = await axios.get(
        apiUrl,
        {
          responseType: "arraybuffer",
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const printWindow = window.open(URL.createObjectURL(blob), "_blank");
      printWindow.print();
    } catch (err) {
      // Handle error silently
    }
  };

  const handlePrintCasePaper = async (dataToPrint, visitType) => {
    try {
      let apiUrl;
      if (visitType === 'IPD') {
        apiUrl = `${config.server.url}/apis/${constants.GET_IPD_CASE_PAPER}/${dataToPrint}`;
      } else {
        apiUrl = `${config.server.url}/apis/${constants.GET_OPD_CASE_PAPER}/${dataToPrint}?showDepartment=${showDepartment}`;
      }
      const response = await axios.get(
        apiUrl,
        {
          responseType: "arraybuffer",
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const printWindow = window.open(URL.createObjectURL(blob), "_blank");
      printWindow.print();
    } catch (err) {
      // Handle error silently
    }
  };

  const handlePrintButtonClick = (dataToPrint, visitType) => {
    if (active === 'Visits') {
      setPendingPrintData({ id: dataToPrint, visitType });
    } else {
      setPendingPrintData(dataToPrint);
    }
    setPrintDialogOpen(true);
  };

  const handlePrintConfirm = () => {
    setPrintDialogOpen(false);
    if (active === 'Visits' && pendingPrintData && typeof pendingPrintData === 'object') {
      handlePrintCasePaper(pendingPrintData.id, pendingPrintData.visitType);
    } else {
      handlePrintOPDCasePaper(pendingPrintData);
    }
    setPendingPrintData(null);
  };

  const handlePrintCancel = () => {
    setPrintDialogOpen(false);
    setPendingPrintData(null);
    setShowDepartment(false); // Reset to default
  };

  const isIPD = selectedVisitData?.consultationType === 'IPD';
  const status = selectedVisitData?.consultationDetails?.status;
  const billStatus = selectedVisitData?.bill?.status;

  // For IPD: open only if status is 'admitted' and bill.status is 'Open'
  const isIPDVisitOpen = isIPD && status === 'admitted' && billStatus === 'Open';
  // For OPD: always enabled
  const isOPD = !isIPD;

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              // padding: 2,
              marginTop: 2,
              borderRadius: 2,
              marginRight: "90px",
            }}
          >
            {/* Back Button */}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigation(-1)}
              sx={{
                color: '#5D6A82',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 'bold',
                padding: 0,
                transition: 'none !important',
                marginBottom: '16px',
              }}
            >
              Back
            </Button>

            {/* Action Buttons (Information | Appointments | Visits) */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  border: "1px solid grey",
                  borderRadius: "4px",
                  p: 1,
                }}
              >
                <Typography
                  sx={{ cursor: "pointer" }}
                  onClick={() => setActive("Information")}
                  color={active === "Information" ? "black" : "#5D6A82"}
                  fontWeight={active === "Information" ? "bold" : "normal"}
                  textDecoration={
                    active === "Information" ? "underline" : "none"
                  }
                >
                  Information
                </Typography>
                <Typography>|</Typography>
                <Typography
                  sx={{ cursor: "pointer" }}
                  onClick={() => setActive("Appointments")}
                  color={active === "Appointments" ? "black" : "#5D6A82"}
                  fontWeight={active === "Appointments" ? "bold" : "normal"}
                  textDecoration={
                    active === "Appointments" ? "underline" : "none"
                  }
                >
                  Appointments
                </Typography>
                <Typography>|</Typography>
                <Typography
                  sx={{ cursor: "pointer" }}
                  onClick={() => setActive("Visits")}
                  color={active === "Visits" ? "black" : "#5D6A82"}
                  fontWeight={active === "Visits" ? "bold" : "normal"}
                  textDecoration={active === "Visits" ? "underline" : "none"}
                >
                  Visits
                </Typography>
              </Box>
              <Box display="flex" gap={2}>
                {active === 'Appointments' && appointmentData && <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={() => handlePrintButtonClick(appointmentData, 'OPD')}
                >
                  Print Case Paper
                </Button>}
                {active === 'Visits' && selectedVisitData && <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={() => handlePrintButtonClick(
                    selectedVisitData?.appointmentId || selectedVisitData?.consultationDetails?.consultationId,
                    selectedVisitData?.visitType ||
                    selectedVisitData?.consultationType ||
                    selectedVisitData?.consultationDetails?.visitType ||
                    selectedVisitData?.consultationDetails?.consultationType ||
                    'OPD')}
                >
                  Print Case Paper
                </Button>}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={() => navigation(`/appointments/${patientId}`)}
                  disabled={isIPD ? !isIPDVisitOpen : false}
                >
                  Add Appointment
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={() => navigation(`/consultation/${patientId}`)}
                  disabled={isIPD ? !isIPDVisitOpen : false}
                >
                  Add Consultation
                </Button>
              </Box>
            </Box>

            {/* Main Content */}
            {active === "Information" && <Information />}
            {active === "Appointments" && <PatientAppointments setAppointmentData={setAppointmentData} />}
            {active === "Visits" && <PatientVisits onSelectVisit={setSelectedVisitData} />}
          </Box>
        </Box>
      </Box>
              <Dialog
          open={printDialogOpen}
          onClose={handlePrintCancel}
          aria-labelledby="print-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="print-dialog-title">Print Case Paper Settings</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Configure what information to include in the case paper:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
              <Typography variant="body2">
                Include Department Field:
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={showDepartment}
                    onChange={(event) => setShowDepartment(event.target.checked)}
                    color="primary"
                  />
                }
                label={showDepartment ? "Yes" : "No"}
                labelPlacement="start"
              />
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Default: Department field is hidden
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handlePrintCancel} color="inherit">
              Cancel
            </Button>
            <Button onClick={handlePrintConfirm} variant="contained" color="primary">
              Generate PDF
            </Button>
          </DialogActions>
        </Dialog>
    </Container>
  );
}

export default PatientInfo;
